/**
 * Live include-filter for a subjects directory.
 *
 * Patterns are filename glob (`*`, `?`), case-insensitive. `{id}` in a file
 * pattern expands to the folder name and to the `sub-` stripped base id, which
 * is how OpenADS example files are named.
 */

export interface DatasetSubject {
  dir: string;
  subjectId: string;
  fileCount: number;
  files: string[];
}

export interface FilterPreset {
  id: string;
  label: string;
  hint: string;
  subjectGlob: string;
  fileGlob: string;
}

export const FILTER_PRESETS: FilterPreset[] = [
  {
    id: "all",
    label: "Every folder",
    hint: "Immediate subfolders that contain NIfTI or JSON",
    subjectGlob: "*",
    fileGlob: "",
  },
  {
    id: "bids",
    label: "BIDS sub-*",
    hint: "Folders named sub-…",
    subjectGlob: "sub-*",
    fileGlob: "",
  },
  {
    id: "dwi",
    label: "*_DWI.nii.gz",
    hint: "Must contain a DWI volume",
    subjectGlob: "*",
    fileGlob: "*_DWI.nii.gz",
  },
  {
    id: "adc",
    label: "*_ADC.nii.gz",
    hint: "Must contain an ADC map",
    subjectGlob: "*",
    fileGlob: "*_ADC.nii.gz",
  },
  {
    id: "pwi",
    label: "*_PWI.nii.gz",
    hint: "Must contain a PWI series",
    subjectGlob: "*",
    fileGlob: "*_PWI.nii.gz",
  },
  {
    id: "isles",
    label: "ISLES*",
    hint: "ISLES-style folder names",
    subjectGlob: "ISLES*",
    fileGlob: "",
  },
];

export function matchGlob(name: string, pattern: string): boolean {
  const trimmed = pattern.trim();
  if (!trimmed) return true;
  return globToRegExp(trimmed).test(name);
}

export function globToRegExp(pattern: string): RegExp {
  let src = "";
  for (const ch of pattern) {
    if (ch === "*") src += ".*";
    else if (ch === "?") src += ".";
    else src += ch.replace(/[.+^${}()|[\]\\]/g, "\\$&");
  }
  return new RegExp(`^${src}$`, "i");
}

export function fileMatches(fileName: string, pattern: string, subjectId: string): boolean {
  const parts = pattern
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length === 0) return true;
  return parts.some((part) => expandFilePattern(part, subjectId).some((p) => matchGlob(fileName, p)));
}

function expandFilePattern(pattern: string, subjectId: string): string[] {
  const baseId = subjectId.replace(/^sub-/i, "");
  const expanded = [pattern.replaceAll("{id}", subjectId), pattern.replaceAll("{id}", baseId)];
  const extra: string[] = [];
  for (const item of expanded) {
    if (/\.nii\.gz$/i.test(item)) extra.push(item.replace(/\.nii\.gz$/i, ".nii"));
  }
  return [...expanded, ...extra];
}

export function subjectMatches(
  subject: DatasetSubject,
  subjectGlob: string,
  fileGlob: string,
): boolean {
  if (!matchGlob(subject.subjectId, subjectGlob || "*")) return false;
  if (!fileGlob.trim()) return true;
  return (subject.files ?? []).some((name) => fileMatches(name, fileGlob, subject.subjectId));
}

export function matchingFiles(subject: DatasetSubject, fileGlob: string): string[] {
  if (!fileGlob.trim()) return subject.files ?? [];
  return (subject.files ?? []).filter((name) => fileMatches(name, fileGlob, subject.subjectId));
}

export function applyFilter(
  subjects: DatasetSubject[],
  subjectGlob: string,
  fileGlob: string,
): DatasetSubject[] {
  return subjects.filter((subject) => subjectMatches(subject, subjectGlob, fileGlob));
}

export function presetIdFor(subjectGlob: string, fileGlob: string): string {
  const found = FILTER_PRESETS.find(
    (preset) => preset.subjectGlob === subjectGlob && preset.fileGlob === fileGlob,
  );
  return found?.id ?? "custom";
}
